import { type FieldValues, FormProvider, useForm } from "react-hook-form";
import { useMutation } from "@tanstack/react-query";
import { api } from "@lib/client";
import { useNavigate } from "@tanstack/react-router";
import { FormStateContext } from "./landing/form-state-context";
import { useState } from "react";
import { type DoubleOptinTicket, type Ticket } from "@lib/ticket";

type DoubleOptinV2FormProps = {
  encodedActivityId: string;
  children: React.ReactNode;
  ticket: Ticket;
};

export function DoubleOptinV2Form({
  encodedActivityId,
  children,
  ...props
}: DoubleOptinV2FormProps) {
  const ticket = props.ticket as DoubleOptinTicket;

  const [disableSubmit, setDisableSubmit] = useState(false);
  const methods = useForm();
  const navigate = useNavigate();
  const { mutateAsync } = useMutation({
    mutationFn: async (formData: FieldValues) => {
      let orderConsentIdsToApprove: string[] = [];

      if (formData["has_confirmation_consents"] !== "true") {
        // This is to handle forms that were created before the confirmation consents block
        // was invented. They just have a form with a single submit button and no checkboxes.
        // So no order consent IDs are submitted, we have to assume they are agreeing to the single
        // consent.
        const [{ order_consent_id: singleOrderConsentId }] =
          ticket.approved_consents;
        orderConsentIdsToApprove = [singleOrderConsentId!];
      } else {
        const formOrderConsentIds = Object.entries(formData).reduce(
          (acc, [key, value]) => {
            if (key.startsWith("confirmation_consent:") && value) {
              return [...acc, key.replace("confirmation_consent:", "")];
            }
            return acc;
          },
          [] as string[],
        );
        orderConsentIdsToApprove = formOrderConsentIds;
      }

      const res = await api.consent.$post({
        json: {
          encodedActivityId,
          orderConsentIds: orderConsentIdsToApprove,
        },
      });

      return res.json();
    },
  });

  const onSubmit = async (formData: FieldValues) => {
    const res = await mutateAsync(formData);
    if (res.success && res.redirect) {
      await navigate(res.redirect);
    }
  };

  return (
    <main>
      <FormStateContext.Provider
        value={{
          validationErrorsWithoutControl: [],
          disableSubmit,
          setDisableSubmit,
        }}
      >
        <FormProvider {...methods}>
          <form
            className="grid gap-8"
            onSubmit={(e) => void methods.handleSubmit(onSubmit)(e)}
          >
            {children}
          </form>
        </FormProvider>
      </FormStateContext.Provider>
    </main>
  );
}
